<!-- Come back to this once teardown state is updated to svelte -->
<!-- Change the state to save attachment with the updateVehicle endpoint -->
<script lang="ts">
	import type { Mediator } from 'types/common'

	import pMap from 'p-map'
	import { graphql } from '$houdini'
	import { getContext } from 'svelte'
	import { hasPermission } from 'utility/permission'

	import Attachments, { type BaseAttachmentFile } from '@isoftdata/svelte-attachments'

	const mediator = getContext<Mediator>('mediator')

	interface Props {
		storeId: number
		vehicleId: number
		fileList: Array<BaseAttachmentFile>
	}

	let { storeId, vehicleId, fileList }: Props = $props()

	async function handleFilesAdded(files: Array<BaseAttachmentFile>) {
		try {
			await pMap(
				files,
				async file => {
					if (file.File) {
						return await createVehicleAttachmentMutation.mutate({
							input: {
								vehicleId,
								public: file.public,
								rank: file.rank,
								file: file.File,
							},
						})
					}
				},
				{ concurrency: 2 },
			)

			mediator.publish('showMessage', {
				heading: 'Attachment(s) Added',
				message: 'The selected attachment(s) have been successfully added.',
				type: 'success',
				time: 10,
			})
		} catch (err) {
			handleError(err)
		}
	}

	async function handleFilesDeleted(files: Array<BaseAttachmentFile>) {
		const fileIdsToDelete = files.reduce((acc: number[], file) => {
			if (file.fileId) {
				acc.push(file.fileId)
			}
			return acc
		}, [])

		try {
			await deleteVehicleAttachmentsMutation.mutate({
				ids: fileIdsToDelete,
			})
			mediator.publish('showMessage', {
				heading: 'Attachment(s) Deleted',
				message: 'The selected attachment(s) have been successfully deleted.',
				type: 'success',
				time: 10,
			})
		} catch (err) {
			handleError(err)
		}
	}

	async function handlePublicAndRankChange(file: BaseAttachmentFile) {
		if (!file.fileId) {
			return
		}

		try {
			const { data } = await updateVehicleAttachmentsMutation.mutate({
				input: {
					id: file.fileId,
					public: file.public,
					rank: file.rank,
				},
			})

			if (data?.updateVehicleAttachment) {
				mediator.publish('showMessage', {
					heading: 'Attachment Updated',
					message: 'The attachment has been successfully updated.',
					type: 'success',
					time: 10,
				})
			}
		} catch (err) {
			handleError(err)
		}
	}

	function handleError(err: any) {
		let message = err.message
		if (typeof err === 'object' && err !== null && typeof err.message === 'string') {
			if (err.extensions?.code === 'DUPLICATE_ATTACHMENT') {
				message = 'Error: Duplicate attachments not allowed'
			}
		}
		mediator.publish('showMessage', {
			heading: 'Error Saving Attachment',
			message,
			type: 'danger',
			time: false,
		})
	}

	const deleteVehicleAttachmentsMutation = graphql(`
		mutation DeleteVehicleAttachments($ids: [UInt!]!) {
			deleteVehicleAttachments(ids: $ids)
		}
	`)

	const createVehicleAttachmentMutation = graphql(`
		mutation CreateVehicleAttachment($input: NewVehicleAttachment!) {
			createVehicleAttachment(input: $input) {
				fileId: id
				rank
				public
				file {
					id
					name
					path: url
					type
					size
					mimeType: mimetype
					extension
					updated
					createdDate: created
				}
			}
		}
	`)

	const updateVehicleAttachmentsMutation = graphql(`
		mutation UpdateVehicleAttachment($input: VehicleAttachmentUpdate!) {
			updateVehicleAttachment(input: $input) {
				fileId: id
				rank
				public
				file {
					id
					name
					path: url
					type
					size
					mimeType: mimetype
					extension
					updated
					createdDate: created
				}
			}
		}
	`)
</script>

<Attachments
	bind:fileList
	modificationDisabled={!hasPermission('VEHICLES_CAN_EDIT_ATTACHMENTS', storeId)}
	filesAdded={handleFilesAdded}
	filesDeleted={handleFilesDeleted}
	publicChange={handlePublicAndRankChange}
	rankChange={handlePublicAndRankChange}
></Attachments>
